/* global React */
/* ──────────────────────────────────────────────────────────────────────
   VisionPage — /vision-2030/
   Editorial alignment paper between Vision 2030's stated commitments
   and Design Zone's services + products. Light theme, brand-canonical
   typography (Outfit weights 600-700, em accents = aqua + bold + NOT
   italic, italic reserved for product names only).
   Namespace: .dz-v30-*
   ────────────────────────────────────────────────────────────────────── */

const { useState: useV30State, useEffect: useV30Effect, useRef: useV30Ref } = React;

/* ─── Reveal-on-scroll wrapper ────────────────────────────────────────── */
function V30Reveal({ children, className }) {
  const [visible, setVisible] = useV30State(false);
  const ref = useV30Ref(null);
  useV30Effect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.10, rootMargin: '0px 0px -60px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={(className || '') + ' dz-v30-reveal' + (visible ? ' is-visible' : '')}>
      {children}
    </div>
  );
}

/* ─── HERO — Aqua Horizon (light, atmospheric, magazine-cover composition)
   Giant ghosted "2030" sits absolute right behind the headline. A breathing
   aqua horizon arc spans the bottom with floating particles. Brand-canonical
   typography: weight-700 title with em-aqua-bold-non-italic accent.
   Cold-open animation is gated behind `.is-cold-open` class which JS toggles
   on after first paint — guarantees the 3s sequence plays on every mount
   (including bfcache restore) instead of being missed during initial paint. ─── */
function V30Hero({ data }) {
  const [coldOpen, setColdOpen] = useV30State(false);

  useV30Effect(() => {
    // Trigger cold-open after first paint (two RAFs ensure layout + paint complete).
    // Also re-trigger on bfcache restore via pageshow event.
    let id1, id2;
    const trigger = () => {
      setColdOpen(false); // reset so re-mount/re-show plays animation again
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setColdOpen(true));
      });
    };
    trigger();

    const onPageShow = (e) => { if (e.persisted) trigger(); };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  return (
    <section className={'dz-v30-hero' + (coldOpen ? ' is-cold-open' : '')}>
      {data.anchor ? (
        data.media && /^\d{4}$/.test(data.anchor) ? (
          <div className="dz-v30-hero__anchor dz-v30x-anchor" aria-hidden="true" dir="ltr">
            <span className="dz-v30x-anchor__l">{data.anchor.slice(0, 2)}</span>
            <span className="dz-v30x-anchor__r">{data.anchor.slice(2)}</span>
          </div>
        ) : <div className="dz-v30-hero__anchor" aria-hidden="true">{data.anchor}</div>
      ) : null}

      <div className="dz-v30-hero__horizon" aria-hidden="true">
        <svg viewBox="0 0 1600 280" preserveAspectRatio="xMidYMid slice">
          <g className="dz-v30-hero__horizon-group">
            <path className="dz-v30-hero__horizon-arc dz-v30-hero__horizon-arc--faint"
                  d="M 0 240 Q 800 60, 1600 240" />
            <path className="dz-v30-hero__horizon-arc dz-v30-hero__horizon-arc--soft"
                  d="M 0 220 Q 800 100, 1600 220" />
            <path className="dz-v30-hero__horizon-arc"
                  d="M 0 200 Q 800 140, 1600 200" />
            {/* animation-delay is set via CSS :nth-of-type — see .dz-v30-hero__particle in home.css.
                Inline animationDelay would override the cold-open stagger. */}
            <circle className="dz-v30-hero__particle" cx="200"  cy="194" r="2.5" />
            <circle className="dz-v30-hero__particle" cx="420"  cy="166" r="2"   />
            <circle className="dz-v30-hero__particle" cx="680"  cy="148" r="3"   />
            <circle className="dz-v30-hero__particle" cx="900"  cy="142" r="2.2" />
            <circle className="dz-v30-hero__particle" cx="1140" cy="156" r="2.8" />
            <circle className="dz-v30-hero__particle" cx="1380" cy="180" r="2"   />
          </g>
        </svg>
      </div>

      <div className={'dz-v30-hero__inner' + (data.media ? ' dz-v30x-herogrid' : '')}>
        <div className="dz-v30x-herocopy">
        <span className="dz-v30-eyebrow">{data.eyebrow}</span>
        <h1 className="dz-v30-hero__title">
          {/* Word 0: aqua accent + attached punct render as one visual unit so
              the punctuation sticks to "Vision 2030" through the reveal */}
          <span className="dz-v30-hero__word" style={{ '--w': 0 }}>
            <span className="dz-v30-em">{data.title.accent}</span>
            {data.title.punct || ''}
          </span>
          {/* Words 1..N: each rest word gets its own stagger index */}
          {(data.title.rest || []).map((w, i) => (
            <React.Fragment key={i}>
              {' '}
              <span className="dz-v30-hero__word" style={{ '--w': i + 1 }}>{w}</span>
            </React.Fragment>
          ))}
          {/* Legacy fallback: if a future caller still passes title.tail, render it bare */}
          {!data.title.rest && data.title.tail ? data.title.tail : null}
        </h1>
        <p className="dz-v30-hero__sub">{data.sub}</p>
        {data.ctas && (
          <div className="dz-v30-hero__cta">
            {data.ctas.map((c, i) => (
              <a key={i} className={'dz-btn ' + (c.outline ? 'dz-btn--profile-outline' : 'dz-btn--aqua')} href={c.href}>{c.label}</a>
            ))}
          </div>
        )}
        </div>
        {data.media && (
          <figure className="dz-v30x-collage">
            <V30Img src={'/assets/projects/' + data.media.main.slug + '.jpg'} alt={data.media.main.alt} className="dz-v30x-collage__main" eager />
            {data.media.side.map((s, i) => (
              <V30Img key={i} src={'/assets/projects/' + s.slug + '.jpg'} alt={s.alt} className={'dz-v30x-collage__side dz-v30x-collage__side--' + (i + 1)} eager />
            ))}
            {data.media.badges.map((b, i) => (
              <figcaption key={i} className={'dz-v30x-badge dz-v30x-badge--' + (i + 1)}><b>{b.num}</b><span>{b.label}</span></figcaption>
            ))}
          </figure>
        )}
      </div>
    </section>
  );
}

/* ─── Shared helpers for the redesigned sections (2026-10-26) ─────────── */
const v30IsAr = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/');
function v30Track(widget, value) { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'vision_widget', widget, value }); }
const v30Reduce = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Project data comes from the projects content (EN or AR); TwinMs/TwinMaq entries come from the map data. */
function v30Project(slug, map) {
  if (map && map.products && map.products[slug]) return Object.assign({ slug }, map.products[slug]);
  const list = (v30IsAr() ? window.DZ_PROJECTS_AR : window.DZ_PROJECTS) || window.DZ_PROJECTS || {};
  const p = (list.items || []).find((x) => x.slug === slug);
  if (!p) return null;
  // The Arabic project list carries no image paths, so take the image from the English list.
  const en = ((window.DZ_PROJECTS || {}).items || []).find((x) => x.slug === slug) || {};
  return { slug, name: p.name, tag: p.categoryLabel || p.scope, category: p.category, img: p.image || en.image,
           href: (v30IsAr() ? '/ar' : '') + '/projects/' + slug + '/' };
}
function V30Img({ src, alt, className, eager, sizes }) {
  const avif = /\/assets\/projects\/[^/]+\.jpg$/.test(src) ? src.replace(/\.jpg$/, '.avif') : null;
  const avifSm = avif ? avif.replace(/\.avif$/, '-mobile.avif') : null;
  return (
    <picture className={className}>
      {avifSm && <source media="(max-width: 700px)" srcSet={avifSm} type="image/avif" />}
      {avif && <source srcSet={avif} type="image/avif" sizes={sizes} />}
      <img src={src} alt={alt || ''} loading={eager ? 'eager' : 'lazy'} decoding="async" />
    </picture>
  );
}
function V30Head({ eyebrow, title, lead, center }) {
  return (
    <header className={'dz-v30x-head' + (center ? ' dz-v30x-head--center' : '')}>
      <span className="dz-v30-eyebrow">{eyebrow}</span>
      <h2 className="dz-v30x-h2">{title.lead}{' '}<span className="dz-v30-em">{title.accent}</span>{title.tail || ''}</h2>
      {lead && <p className="dz-v30x-lead">{lead}</p>}
    </header>
  );
}

/* ─── MAP — every project on a map of the Kingdom, filterable by discipline ─── */
const V30_KSA = [[34.95,29.35],[36.07,29.19],[36.75,29.87],[37.5,30.0],[37.67,30.34],[38.0,30.5],[37.0,31.5],[38.8,32.0],[39.2,32.16],[40.4,31.95],[41.44,31.37],[42.9,30.5],[44.7,29.18],[46.4,29.1],[47.45,29.0],[48.42,28.53],[48.6,28.1],[49.3,27.4],[49.7,26.9],[50.15,26.6],[50.2,26.1],[50.6,25.4],[50.8,24.75],[51.2,24.55],[51.6,24.25],[52.6,22.95],[55.2,22.7],[55.0,20.0],[52.0,19.0],[49.1,18.6],[48.2,18.2],[47.6,17.5],[47.0,16.95],[46.3,17.25],[45.4,17.3],[44.6,17.4],[43.6,17.5],[43.3,17.0],[43.1,16.6],[42.8,16.4],[42.6,16.8],[42.4,17.4],[41.7,18.0],[41.2,19.0],[40.75,19.8],[40.2,20.4],[39.6,20.95],[39.15,21.5],[39.1,22.2],[38.95,22.8],[38.5,23.4],[38.1,24.0],[37.6,24.5],[37.3,25.0],[36.9,25.6],[36.6,26.0],[36.3,26.5],[35.8,27.2],[35.4,27.8],[35.1,28.1],[34.6,28.1],[34.8,28.6]];
const v30X = (lon) => (lon - 34) * 40, v30Y = (lat) => (33 - lat) * 40;
function V30Map({ data, ui }) {
  const [filter, setFilter] = useV30State('all');
  const [city, setCity] = useV30State('riyadh');
  const itemsOf = (c) => c.items.map((s) => v30Project(s, data)).filter((p) => p && (filter === 'all' || p.category === filter));
  useV30Effect(() => {
    const cur = data.cities.find((c) => c.id === city);
    if (cur && itemsOf(cur).length) return;
    const first = data.cities.find((c) => itemsOf(c).length);
    if (first) setCity(first.id);
  }, [filter]);
  const active = data.cities.find((c) => c.id === city) || data.cities[0];
  const activeItems = itemsOf(active);
  const outline = 'M' + V30_KSA.map(([lo, la]) => v30X(lo).toFixed(0) + ' ' + v30Y(la).toFixed(0)).join(' L') + ' Z';
  const pick = (id) => { setCity(id); v30Track('map_city', id); };
  return (
    <section className="dz-v30x dz-v30x--mist" id="v30-map">
      <V30Reveal>
        <div className="dz-v30x-inner">
          <V30Head eyebrow={data.eyebrow} title={data.title} lead={data.lead} />
          <div className="dz-v30x-chips" role="group" aria-label={data.eyebrow}>
            {data.filters.map((f) => (
              <button type="button" key={f.k} className={'dz-v30x-chip' + (filter === f.k ? ' is-on' : '')} aria-pressed={filter === f.k}
                onClick={() => { setFilter(f.k); v30Track('map_filter', f.k); }}>{f.label}</button>
            ))}
          </div>
          <div className="dz-v30x-map">
            <div className="dz-v30x-map__stage" dir="ltr">
              <svg viewBox="0 0 880 680" aria-hidden="true">
                <defs>
                  <linearGradient id="v30-land" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FFFFFF" /><stop offset="1" stopColor="#E3EEF3" /></linearGradient>
                </defs>
                <path d={outline} fill="url(#v30-land)" stroke="#9BC4D6" strokeWidth="2" strokeLinejoin="round" />
                <text x={v30X(37.6)} y={v30Y(20.2)} className="dz-v30x-map__sea">{v30IsAr() ? 'البحر الأحمر' : 'Red Sea'}</text>
                <text x={v30X(51.2)} y={v30Y(27.6)} className="dz-v30x-map__sea">{v30IsAr() ? 'الخليج العربي' : 'Arabian Gulf'}</text>
              </svg>
              {data.cities.map((c) => {
                const n = itemsOf(c).length;
                if (!n) return null;
                return (
                  <button type="button" key={c.id} className={'dz-v30x-pin is-' + (c.side || 'b') + (city === c.id ? ' is-on' : '') + (n ? '' : ' is-empty')}
                    style={{ left: (v30X(c.lon) / 880 * 100) + '%', top: (v30Y(c.lat) / 680 * 100) + '%' }}
                    onClick={() => pick(c.id)} aria-pressed={city === c.id} disabled={!n}
                    aria-label={c.name + ' · ' + n + ' ' + (n === 1 ? ui.project : ui.projects)}>
                    <span className="dz-v30x-pin__dot">{n || ''}</span>
                    <span className="dz-v30x-pin__label" dir="auto">{c.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="dz-v30x-map__panel" aria-live="polite">
              <p className="dz-v30x-map__city">{active.name}<span>{activeItems.length} {activeItems.length === 1 ? ui.project : ui.projects}</span></p>
              {activeItems.length === 0 && <p className="dz-v30x-map__hint">{data.hint}</p>}
              <ul className="dz-v30x-map__list">
                {activeItems.map((p) => (
                  <li key={p.slug}>
                    <a className="dz-v30x-proj" href={p.href}>
                      <V30Img src={p.img} alt={p.name} className="dz-v30x-proj__img" />
                      <span className="dz-v30x-proj__txt"><b>{p.name}</b><small>{p.tag}</small></span>
                    </a>
                  </li>
                ))}
              </ul>
              {data.outside && (filter === 'all' || filter === 'interior') && (
                <p className="dz-v30x-map__outside">{data.outside.label}</p>
              )}
            </div>
          </div>
        </div>
      </V30Reveal>
    </section>
  );
}

/* ─── PILLARS — tabs with the Vision 2030 target as an animated bar ─── */
function V30Pillars({ data, map, ui }) {
  const [i, setI] = useV30State(0);
  const [go, setGo] = useV30State(false);
  const ref = useV30Ref(null);
  useV30Effect(() => {
    const el = ref.current; if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setGo(true); o.disconnect(); } }, { threshold: 0.3 });
    o.observe(el); return () => o.disconnect();
  }, []);
  const p = data.items[i], k = p.kpi || {};
  const scale = k.unit ? Math.max(k.to * 1.15, 1) : 100;
  const fromW = (k.from / scale) * 100, toW = (k.to / scale) * 100;
  return (
    <section className="dz-v30x dz-v30x--white" ref={ref}>
      <V30Reveal>
        <div className="dz-v30x-inner">
          <V30Head eyebrow={data.eyebrow} title={data.title} />
          <div className="dz-v30x-tabs" role="tablist" aria-label={data.eyebrow}>
            {data.items.map((t, j) => (
              <button type="button" role="tab" key={j} aria-selected={i === j} className={'dz-v30x-tab' + (i === j ? ' is-on' : '')}
                onClick={() => { setI(j); v30Track('pillar', t.num); }}>
                <span className="dz-v30x-tab__n">{t.num.split('/')[0].trim()}</span>{t.num.split('/')[1] ? t.num.split('/')[1].trim() : t.name}
              </button>
            ))}
          </div>
          <div className="dz-v30x-pillar" role="tabpanel">
            <div className="dz-v30x-pillar__copy">
              <h3>{p.name}</h3>
              <p dangerouslySetInnerHTML={{ __html: p.themes }} />
            </div>
            <div className="dz-v30x-kpi">
              <p className="dz-v30x-kpi__label">{ui.target} · {k.label}</p>
              <div className="dz-v30x-kpi__bar">
                <span className="dz-v30x-kpi__from" style={{ width: (go || v30Reduce() ? fromW : 0) + '%' }} />
                <span className="dz-v30x-kpi__to" style={{ width: (go || v30Reduce() ? toW : 0) + '%' }} />
              </div>
              <div className="dz-v30x-kpi__vals">
                <span><small>{ui.today}</small><b>{k.fromText || (k.from + k.unit)}</b></span>
                <span><small>{ui.goal}</small><b className="is-goal">{k.toText || (k.to + k.unit)}</b></span>
              </div>
            </div>
            <ul className="dz-v30x-thumbs">
              {(p.projects || []).map((s) => { const pr = v30Project(s, map); return pr && (
                <li key={s}><a href={pr.href}><V30Img src={pr.img} alt={pr.name} /><span>{pr.name}</span></a></li>
              ); })}
            </ul>
          </div>
        </div>
      </V30Reveal>
    </section>
  );
}

/* ─── PACT — six commitments as photo cards; "What we deliver" reveals our part ─── */
function V30Pact({ data, ui }) {
  const [open, setOpen] = useV30State(-1);
  return (
    <section className="dz-v30x dz-v30x--sand" id="v30-pact">
      <V30Reveal>
        <div className="dz-v30x-inner">
          <V30Head eyebrow={data.eyebrow} title={data.title} lead={data.lead} />
          <ol className="dz-v30x-cards">
            {data.articles.map((a, i) => (
              <li key={i} className={'dz-v30x-card' + (open === i ? ' is-open' : '')} style={{ '--v30-i': i }}>
                <div className="dz-v30x-card__media">
                  <V30Img src={a.img} alt="" />
                  <span className="dz-v30x-card__num">{a.num}</span>
                  <span className="dz-v30x-card__tag">{a.vision.tag}</span>
                </div>
                <div className="dz-v30x-card__body">
                  <p className="dz-v30x-card__vision">{a.vision.short}</p>
                  <button type="button" className="dz-v30x-card__btn" aria-expanded={open === i}
                    onClick={() => { setOpen(open === i ? -1 : i); if (open !== i) v30Track('commitment', a.num); }}>
                    {open === i ? ui.hideDeliver : ui.deliver}
                  </button>
                  <div className="dz-v30x-card__dz" hidden={open !== i}>
                    <p>{a.dz.short}</p>
                    <ul>
                      {a.dz.anchors.map((x, j) => (
                        <li key={j}>{x.href ? <a href={x.href}>{x.name}</a> : x.name}<small>{x.loc}</small></li>
                      ))}
                    </ul>
                  </div>
                  <details className="dz-v30x-more">
                    <summary>{ui.readMore}</summary>
                    <p>{a.vision.quote} <small>{a.vision.source}</small></p>
                    <p dangerouslySetInnerHTML={{ __html: a.dz.claim }} />
                  </details>
                </div>
              </li>
            ))}
          </ol>
          {data.ctaLink && <p className="dz-v30-cta-link" dangerouslySetInnerHTML={{ __html: data.ctaLink }} />}
        </div>
      </V30Reveal>
    </section>
  );
}

/* ─── TIMELINE — 2013 to 2030, revealed as the visitor scrolls ─── */
function V30Timeline({ data }) {
  const ref = useV30Ref(null);
  useV30Effect(() => {
    const el = ref.current; if (!el) return;
    const items = el.querySelectorAll('.dz-v30x-tl__item');
    if (v30Reduce()) { items.forEach((n) => n.classList.add('is-in')); return; }
    const o = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); o.unobserve(e.target); } }), { threshold: 0.4 });
    items.forEach((n) => o.observe(n)); return () => o.disconnect();
  }, []);
  return (
    <section className="dz-v30x dz-v30x--navy">
      <div className="dz-v30x-inner" ref={ref}>
        <V30Head eyebrow={data.eyebrow} title={data.title} center />
        <ol className="dz-v30x-tl">
          {data.items.map((t, i) => (
            <li key={i} className={'dz-v30x-tl__item is-' + t.kind}>
              <span className="dz-v30x-tl__year">{t.year}</span>
              <span className="dz-v30x-tl__dot" aria-hidden="true" />
              <p className="dz-v30x-tl__text">{t.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ─── SIGNATURES — three differentiators as short cards with details on demand ─── */
function V30Signatures({ data, ui }) {
  return (
    <section className="dz-v30x dz-v30x--white">
      <V30Reveal>
        <div className="dz-v30x-inner">
          <V30Head eyebrow={data.eyebrow} title={data.title} />
          <div className="dz-v30x-signs">
            {data.items.map((s, i) => (
              <article key={i} className="dz-v30x-sign">
                <span className="dz-v30x-sign__n">{s.num}</span>
                <h3 dangerouslySetInnerHTML={{ __html: s.claim }} />
                <p>{s.short}</p>
                <details className="dz-v30x-more">
                  <summary>{ui.readMore}</summary>
                  <p dangerouslySetInnerHTML={{ __html: s.body }} />
                </details>
              </article>
            ))}
          </div>
        </div>
      </V30Reveal>
    </section>
  );
}

/* ─── READINESS — five questions, a readiness level and the first step ─── */
function V30Ready({ data }) {
  const [ans, setAns] = useV30State({});
  const done = data.questions.every((_, i) => ans[i] !== undefined);
  const score = Object.values(ans).reduce((s, v) => s + v, 0);
  const lvl = score <= 2 ? 0 : score <= 5 ? 1 : score <= 8 ? 2 : 3;
  const sent = useV30Ref(false);
  useV30Effect(() => { if (done && !sent.current) { sent.current = true; v30Track('tender_readiness', data.levels[lvl]); } }, [done]);
  return (
    <section className="dz-v30x dz-v30x--mist" id="v30-ready">
      <V30Reveal>
        <div className="dz-v30x-inner dz-v30x-ready">
          <V30Head eyebrow={data.eyebrow} title={data.title} lead={data.lead} />
          <div className="dz-v30x-ready__box">
            {data.questions.map((q, i) => (
              <fieldset key={i} className="dz-v30x-q">
                <legend>{(i + 1) + '. ' + q.q}</legend>
                <div className="dz-v30x-opts">
                  {q.opts.map((o, j) => (
                    <label key={j} className={'dz-v30x-opt' + (ans[i] === j ? ' is-on' : '')}>
                      <input type="radio" name={'v30q' + i} checked={ans[i] === j} onChange={() => setAns(Object.assign({}, ans, { [i]: j }))} />
                      <span>{o}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
            <div className="dz-v30x-res" aria-live="polite">
              {!done ? <p className="dz-v30x-res__wait">{data.wait}</p> : (
                <React.Fragment>
                  <div className="dz-v30x-gauge" aria-hidden="true">{[0, 1, 2, 3].map((n) => <span key={n} className={n <= lvl ? 'is-on' : ''} />)}</div>
                  <p className="dz-v30x-res__lvl">{data.levels[lvl]}</p>
                  <p className="dz-v30x-res__step">{data.steps[lvl]}</p>
                  <a className="dz-btn dz-btn--aqua" href="#inquiry">{data.cta}</a>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </V30Reveal>
    </section>
  );
}

/* ─── TRUST — client quote and the Vision 2030 client logos ─── */
function V30Trust({ data }) {
  return (
    <section className="dz-v30x dz-v30x--sand">
      <V30Reveal>
        <div className="dz-v30x-inner dz-v30x-trust">
          <span className="dz-v30-eyebrow">{data.eyebrow}</span>
          <blockquote className="dz-v30x-quote">
            <p>{data.quote.text}</p>
            <footer><b>{data.quote.name}</b> · {data.quote.role}, {data.quote.company}</footer>
          </blockquote>
          <ul className="dz-v30x-logos">
            {data.logos.map((l, i) => <li key={i}><img src={l.src} alt={l.alt} loading="lazy" decoding="async" /></li>)}
          </ul>
        </div>
      </V30Reveal>
    </section>
  );
}

/* ─── CLOSING CTA ────────────────────────────────────────────────────── */
function V30Closing({ data }) {
  if (data.sub) return (
    <section className="dz-v30x dz-v30x--white dz-v30x-close">
      <V30Reveal>
        <div className="dz-v30x-close__card">
          {data.img && <V30Img src={data.img} alt="" className="dz-v30x-close__bg" />}
          <div className="dz-v30x-close__body">
            <span className="dz-v30-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-v30x-close__title">{data.line} <em>{data.accent}</em></h2>
            <p className="dz-v30x-close__sub">{data.sub}</p>
            <ul className="dz-v30x-close__points">
              {data.points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
            <div className="dz-v30x-close__ctas">
              <a className="dz-btn dz-btn--aqua" href={data.cta.href} onClick={() => v30Track('closing_cta', 'form')}>{data.cta.label}</a>
              {data.cta2 && <a className="dz-v30x-close__ghost" href={data.cta2.href}>{data.cta2.label}</a>}
            </div>
          </div>
        </div>
      </V30Reveal>
    </section>
  );
  return (
    <section className="dz-v30-close">
      <p className="dz-v30-close__line">
        {data.line}{' '}
        <span className="dz-v30-em">{data.accent}</span>
      </p>
      <a className="dz-v30-close__btn" href={data.cta.href}>
        {data.cta.label}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" aria-hidden="true">
          <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </section>
  );
}

/* ─── Page root ──────────────────────────────────────────────────────── */
function VisionPage() {
  const data = window.DZ_VISION;
  if (!data) {
    return (
      <main className="dz-v30 dz-v30--missing">
        <h1>Vision 2030 · Alignment</h1>
        <p>Content layer failed to load.</p>
      </main>
    );
  }
  return (
    <main className="dz-v30" id="dz-main">
      <V30Hero       data={data.hero} />
      {data.map      && <V30Map      data={data.map} ui={data.ui} />}
      <V30Pillars    data={data.pillars} map={data.map} ui={data.ui || {}} />
      <V30Pact       data={data.pact} ui={data.ui || {}} />
      {data.timeline && <V30Timeline data={data.timeline} />}
      <V30Signatures data={data.signatures} ui={data.ui || {}} />
      {data.readiness && <V30Ready   data={data.readiness} />}
      {data.trust    && <V30Trust    data={data.trust} />}
      {data.faq && data.faq.items && data.faq.items.length > 0 && (
        <V30Faq data={data.faq} />
      )}
      <V30Closing    data={data.closing} />
    </main>
  );
}

/* ── FAQ section — slots between Signatures and Closing.
   Reuses .dz-slp__section--faq + .dz-slp__faq styles for visual
   consistency with other FAQ blocks site-wide. */
function V30Faq({ data }) {
  return (
    <section className="dz-v30x dz-v30x--white dz-v30x-faq" id="v30-faq">
      <V30Reveal>
        <div className="dz-v30x-inner">
          <span className="dz-v30-eyebrow">{data.eyebrow || 'VISION 2030 · FAQ'}</span>
          <h2 className="dz-v30x-faq__title">{data.title}</h2>
          <div className="dz-v30x-faq__list">
            {data.items.map((qa, i) => (
              <details className="dz-v30x-faq__item" key={i} open={i === 0}>
                <summary className="dz-v30x-faq__q">{qa.q}</summary>
                <div className="dz-v30x-faq__a" dangerouslySetInnerHTML={{ __html: qa.a }} />
              </details>
            ))}
          </div>
        </div>
      </V30Reveal>
    </section>
  );
}

window.VisionPage = VisionPage;
